import AppStoreLink from './AppStoreLink'
import GooglePlayLink from './GooglePlayLink'
import EventStoreLink from '@/app/events/EventStoreLink'
import type { EventParams } from '@/utils/analytics'

/**
 * The official App Store and Google Play badges, side by side.
 *
 * Apple and Google both require their badge artwork to be used unaltered,
 * so the artwork is the downloaded originals in /public/badges and only the
 * height is set here. Each badge routes through AppStoreLink / GooglePlayLink,
 * so impressions and clicks keep the same `source` attribution as before.
 */
export type StoreBadgeSize = 'sm' | 'md' | 'lg'

const HEIGHT: Record<StoreBadgeSize, string> = {
  sm: 'h-10',
  md: 'h-12',
  lg: 'h-12 sm:h-14',
}

const ALIGN = {
  start: 'justify-start',
  center: 'justify-center',
  // Centered on phones, aligned with the copy column from md up.
  responsive: 'justify-center md:justify-start',
} as const

export interface StoreBadgesProps {
  source: string
  ctaPosition?: string
  trackingParams?: EventParams
  /** Custom product page or event URL for the App Store badge. */
  appStoreHref?: string
  /** In-app event: upgrade the App Store link to the event once it is live. */
  event?: { eventId: string; publishAtIso: string }
  size?: StoreBadgeSize
  align?: keyof typeof ALIGN
  /** Adds a hairline ring so the black badges keep their edge on dark grounds. */
  onDark?: boolean
  className?: string
}

export default function StoreBadges({
  source,
  ctaPosition,
  trackingParams,
  appStoreHref,
  event,
  size = 'md',
  align = 'start',
  onDark = false,
  className = '',
}: StoreBadgesProps) {
  const h = HEIGHT[size]
  const linkClass = `store-badge group relative inline-flex shrink-0 rounded-[0.55rem] ${
    onDark ? 'ring-1 ring-white/20' : ''
  } focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
    onDark ? 'focus-visible:outline-goldLight' : 'focus-visible:outline-forest'
  }`
  const appBadge = (
    // eslint-disable-next-line @next/next/no-img-element -- official SVG artwork, rendered unaltered
    <img src="/badges/app-store-black.svg" alt="Download on the App Store" width={120} height={40} className={`${h} w-auto block`} />
  )
  return (
    <div className={`flex flex-wrap items-center gap-3 ${ALIGN[align]} ${className}`}>
      {event ? (
        <EventStoreLink
          eventId={event.eventId}
          publishAtIso={event.publishAtIso}
          source={source}
          ctaPosition={ctaPosition}
          trackingParams={trackingParams}
          className={linkClass}
        >
          {appBadge}
        </EventStoreLink>
      ) : (
        <AppStoreLink
          source={source}
          href={appStoreHref}
          ctaPosition={ctaPosition}
          trackingParams={trackingParams}
          className={linkClass}
        >
          {appBadge}
        </AppStoreLink>
      )}
      <GooglePlayLink
        source={source}
        ctaPosition={ctaPosition}
        trackingParams={trackingParams}
        className={linkClass}
      >
        {/* eslint-disable-next-line @next/next/no-img-element -- official badge artwork, rendered unaltered */}
        <img src="/badges/google-play.png" alt="Get it on Google Play" width={134} height={40} className={`${h} w-auto block`} />
      </GooglePlayLink>
    </div>
  )
}
